/**
 * Campaign Rules Tab Component
 * Shows campaign rules, terms, and participation guidelines
 */

import { type FC, memo } from 'react';

import LoadingIndicator from 'shared/components/LoadingIndicator';
import CampaignTermsInfo from './TermsInfoModal';
import { useTranslation } from 'react-i18next';
import { useSelectedCampaign, usePrizesCount, useAutoJoin, PRIZE_POP_CAMPAIGN_TYPE } from 'features/campaigns';
import { testIds } from 'core/testIds';
import { formatDate, formatTime } from 'shared/utils/dateHelpers';
import { formatCurrencyValue, formatNumberWithSpaces } from 'shared/utils/helpers';
import type { PrizePopIntervalTimeRange, IPrizePopCampaign } from '../../../../../types/campaign.types';
import TransWithFallback from 'shared/components/Translations/TransWrapper';

import styles from './RulesTab.module.css';

interface CampaignRulesTabProps {
  campaignId?: string;
}

const RulesTab: FC<CampaignRulesTabProps> = () => {
  const campaign = useSelectedCampaign() as IPrizePopCampaign;
  const prizesCount = usePrizesCount();
  const autoJoin = useAutoJoin(PRIZE_POP_CAMPAIGN_TYPE);
  const { t } = useTranslation();

  if (!campaign) {
    return (
      <div className={styles.rulesWrapper}>
        <div className={styles.loaderContainer}>
          <LoadingIndicator show={true} absolute />
        </div>
      </div>
    );
  }

  // Group intervals by date range
  const groupedIntervals = campaign.intervals.reduce((acc, interval) => {
    const startDate = formatDate(interval.range.startRangeDate);
    const endDate = formatDate(interval.range.endRangeDate);
    const fromCapitalized = (t('campaigns.from', 'from'))[0].toUpperCase() + (t('campaigns.from', 'from')).slice(1);
    const key = startDate === endDate ? startDate : `${fromCapitalized} ${startDate} ${t('campaigns.to', 'to')} ${endDate}`;
    
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(...interval.hoursRange);
    return acc;
  }, {} as Record<string, PrizePopIntervalTimeRange[]>);

  return (
    <div className={styles.rulesWrapper}>
      <div className={styles.contentContainerWrapper}>
        <div className={styles.contentContainer} data-testid={testIds.campaigns.rulesContent(PRIZE_POP_CAMPAIGN_TYPE)}>
          <div className={styles.textSection}>
          {/* General Information */}
            <div>
              <div className={styles.title}>{t('campaigns.general_information', 'General Information')}</div>
              <div className={styles.description}>
                <div>
                  {t('campaigns.total_prize_pool', 'Total Prize Pool')}: <span className={styles.highlightedText}>{formatNumberWithSpaces(campaign.prizeFund)} {campaign.baseCurrency}.</span>
                </div>
                <div>
                  {t('campaigns.total_number_of_prizes', 'Total number of prizes')}: <span className={styles.highlightedText}>{formatNumberWithSpaces(prizesCount.total)}.</span>
                </div>
              </div>
            </div>

          {/* Participation Rules */}
          <div>
            <div className={styles.title}>{t('campaigns.participation_rules', 'Participation Rules')}</div>
            <div className={styles.description}>
              {!autoJoin && (
                <div>
                  <TransWithFallback
                    i18nKey="campaigns.click_join_in_button"
                    components={{ b: <span className={styles.highlightedText} /> }}
                  />
                </div>
              )}
              {campaign.minStake != null && (
              <div>
                {t('campaigns.minimum_stake_amount', 'Minimum stake amount is')}: <span className={styles.highlightedText}>{formatCurrencyValue(campaign.minStake, 100)} {campaign.baseCurrency}.</span>
              </div>
              )}
            </div>
          </div>

          {/* Time Intervals */}
          {campaign.intervals && campaign.intervals.length > 0 && (
            <div>
              {Object.entries(groupedIntervals).map(([dateRange, timeSlots], index) => (
                <div key={index} className={styles.intervalSection}>
                  <div className={styles.title}>{dateRange}</div>
                  <ul className={styles.timeSlotList}>
                    {timeSlots.map((slot, slotIndex) => (
                      <li key={slotIndex} className={styles.timeSlotItem}>
                        {t('campaigns.from', 'from')} <span className={styles.highlightedText}>{formatTime(slot.startTime)}</span> {t('campaigns.to', 'to')}{' '}
                        <span className={styles.highlightedText}>{formatTime(slot.endTime)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
          <CampaignTermsInfo
            campaignId={campaign.id}
            campaignName={campaign.name}
            terms={campaign.terms}
          />
        </div>
      </div>
    </div>
  );
};

export default memo(RulesTab);
